import { AppDataState, Session } from './app-data.types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppError } from '../../../api/appError';
import { DataStorageObject } from '../../../models/data-storage.model';
import {
  deleteFile,
  fetchDataStorageData,
  setDocumentsSubmitted,
  startLegitimation,
  uploadFiles,
  validateLegitimationResult
} from './app-data.actions';

export const SESSION_CACHE_KEY = 'cvuuk/session';

export const getSessionInitialState = (): Session | null => {
  const searchParams = new URLSearchParams(window.location.search);
  const isNew = Boolean(searchParams.get('id'));
  if (isNew) {
    sessionStorage.removeItem(SESSION_CACHE_KEY);
  }
  const sessionJSON = sessionStorage.getItem(SESSION_CACHE_KEY) as string;
  return isNew || !sessionJSON ? null : (JSON.parse(sessionJSON) as Session);
};

export const initialState: AppDataState = {
  dataStorage: null,

  appError: null,
  session: getSessionInitialState(),
  ui: {
    dataLoadingCounter: 0,
    fileUploadPendingCounter: 0,
    isDataStorageDataLoaded: false,
    didUploadFail: false,
    isUploadPageLoaded: false,
    isAcknowledgementPageLoaded: false
  }
};

export const appDataSlice = createSlice({
  name: 'appData',
  initialState,
  reducers: {
    setSession: (state: AppDataState, action: PayloadAction<Session>) => {
      sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(action.payload));
      state.session = action.payload;
    },
    setUiUploadError: (state: AppDataState, action: PayloadAction<boolean>) => {
      state.ui.didUploadFail = action.payload;
    },
    setDataStorage: (state: AppDataState, action: PayloadAction<DataStorageObject>) => {
      state.dataStorage = action.payload;
    },
    setDataStorageDataIsLoading: (state: AppDataState, action: PayloadAction<boolean>) => {
      state.ui.dataLoadingCounter += action.payload ? 1 : -1;
    },
    setAppError: (state: AppDataState, action: PayloadAction<AppError>) => {
      state.appError = action.payload;
    },
    setUploadPageLoaded: (state: AppDataState, action: PayloadAction<boolean>) => {
      state.ui.isUploadPageLoaded = action.payload;
    },
    setAcknowledgementPageLoaded: (state: AppDataState, action: PayloadAction<boolean>) => {
      state.ui.isAcknowledgementPageLoaded = action.payload;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchDataStorageData.pending, state => {
      state.ui.dataLoadingCounter += 1;
    });
    builder.addCase(fetchDataStorageData.fulfilled, (state, action) => {
      state.dataStorage = action.payload;
      state.ui.dataLoadingCounter -= 1;
      state.ui.isDataStorageDataLoaded = true;
    });
    builder.addCase(fetchDataStorageData.rejected, (state, action) => {
      state.appError = {
        message: action.error.message,
        errorCausingURL: window.location.pathname,
        errorCode: action.error.code
      };
      state.ui.dataLoadingCounter -= 1;
    });

    builder.addCase(startLegitimation.pending, state => {
      state.ui.dataLoadingCounter += 1;
    });
    builder.addCase(startLegitimation.rejected, (state, action) => {
      state.appError = {
        message: action.error.message,
        errorCausingURL: window.location.pathname,
        errorCode: action.error.code
      };
      state.ui.dataLoadingCounter -= 1;
    });

    builder.addCase(validateLegitimationResult.pending, state => {
      state.ui.dataLoadingCounter += 1;
    });
    builder.addCase(validateLegitimationResult.rejected, (state, action) => {
      state.appError = {
        message: action.error.message,
        errorCausingURL: window.location.pathname,
        errorCode: action.error.code
      };
      state.ui.dataLoadingCounter -= 1;
    });
    builder.addCase(validateLegitimationResult.fulfilled, (state, action) => {
      state.ui.dataLoadingCounter -= 1;
      state.dataStorage = action.payload;
    });

    builder.addCase(uploadFiles.pending, state => {
      state.ui.fileUploadPendingCounter += 1;
      state.ui.didUploadFail = false;
    });
    builder.addCase(uploadFiles.rejected, state => {
      state.ui.didUploadFail = true;
      state.ui.fileUploadPendingCounter -= 1;
    });
    builder.addCase(uploadFiles.fulfilled, (state, action) => {
      state.ui.fileUploadPendingCounter -= 1;
      state.fileIds = action.payload;
    });

    builder.addCase(deleteFile.pending, state => {
      state.ui.didUploadFail = false;
      state.ui.fileUploadPendingCounter += 1;
    });
    builder.addCase(deleteFile.rejected, state => {
      state.ui.didUploadFail = true;
      state.ui.fileUploadPendingCounter -= 1;
    });
    builder.addCase(deleteFile.fulfilled, (state, action) => {
      state.ui.fileUploadPendingCounter -= 1;
      state.dataStorage = action.payload;
    });

    builder.addCase(setDocumentsSubmitted.pending, state => {
      state.ui.dataLoadingCounter += 1;
    });
    builder.addCase(setDocumentsSubmitted.rejected, (state, action) => {
      state.appError = {
        message: action.error.message,
        errorCausingURL: window.location.pathname,
        errorCode: action.error.code
      };
      state.ui.dataLoadingCounter -= 1;
    });
    builder.addCase(setDocumentsSubmitted.fulfilled, (state, action) => {
      state.ui.dataLoadingCounter -= 1;
      state.dataStorage = action.payload;
    });
  }
});

export const {
  setAppError,
  setUiUploadError,
  setDataStorage,
  setDataStorageDataIsLoading,
  setSession,
  setUploadPageLoaded,
  setAcknowledgementPageLoaded
} = appDataSlice.actions;
